import { ReactNode } from 'react';

import { Method } from 'axios';

import { HTTP_METHODS, isCrud } from 'utils/http.utils';

import { HttpError } from './HttpError';
import { ErrorCode } from './serverErrorCode';

export enum ErrorName {
  Unauthorized = 'Unauthorized',
  MissingPermission = 'MissingPermission',
  NoNetwork = 'NoNetworkError',
  NoServerResponse = 'NoServerResponse',
  HttpError = 'HttpError',
  RequestCancelled = 'RequestCancelled',
  MissingEntity = 'MissingEntity',
  PatientNoExist = 'PatientNoExist',
  InvalidRequest = 'InvalidRequest', // see EH-3303
  InvalidEmail = 'InvalidEmail',
  InvalidPhone = 'InvalidPhone',
  NotMobilePhone = 'NotMobilePhone',
  PhoneInUse = 'PhoneInUse',
  DuplicationError = 'DuplicationError', // see EH-3303
  MrnInUse = 'MrnInUse',
  OverlappingIntervals = 'OverlappingIntervals',
  IntervalOutOfAllowedScope = 'IntervalOutOfAllowedScope',
  ResolveTicketsConflict = 'ResolveTicketsConflict',
  ResolveClosedTicket = 'ResolveClosedTicket',
  UserMissingData = 'UserMissingData',
  DuplicateEpisodeName = 'DuplicateEpisodeName',
  DuplicateEpisodeEndReason = 'DuplicateEpisodeEndReason',
  InvalidEpisodeDuration = 'InvalidEpisodeDuration',
  EnrollInActiveCm = 'EnrollInActiveCm',
  TicketDoublePickup = 'TicketDoublePickup',
  DuplicateReason = 'DuplicateReason',
  UserBlockSendingSms = 'UserBlockSendingSms',
  FailedSendingCallToEmr = 'FailedSendingCallToEmr',
  FailedToGetHistory = 'FailedToGetHistory',
  AuditFailed = 'AuditFailed',
  CareIntervalFailed = 'CareIntervalFailed',
  UpdateIntroFeatureFailed = 'UpdateIntroFeatureFailed',
  DraftCallActionFailed = 'DraftCallActionFailed',
  ThoughtSpot = 'ThoughtSpot',
  SSOAuthenticatedButNotRegistered = 'SSOAuthenticatedButNotRegistered',
  DuplicateTicketSubTypeOperatorName = 'DuplicateTicketSubTypeOperatorName',
  DuplicateTicketTypeDisplayNameForPatientApp = 'DuplicateTicketTypeDisplayNameForPatientApp',
  TicketConnectedToDraft = 'TicketConnectedToDraft',
  ThoughtSpotNewUser = 'ThoughtSpotNewUser',
  BulkInviteInProgress = 'BulkInviteInProgress',
  SummaryCharacterLimit = 'SummaryCharacterLimit',
  LandPhoneOnAutoProtocol = 'LandPhoneOnAutoProtocol',
  FailedToSendSms = 'FailedToSendSms',
  DuplicateRoleName = 'DuplicateRoleName',
  FilterSetActionFailed = 'FilterSetActionFailed',
  DuplicateEmrPatientFound = 'DuplicateEmrPatientFound',
  NoDuplicateEmrPatientFound = 'NoDuplicateEmrPatientFound',
  MultipleDuplicateEmrPatientFound = 'MultipleDuplicateEmrPatientFound',
  ResolvingFailedOutboundMessages = 'ResolvingFailedOutboundMessages',
  ResolvingPendingOutboundMessages = 'ResolvingPendingOutboundMessages',
  ResolvingUnreadInboundMessages = 'ResolvingUnreadInboundMessages',
  PatientCannotReplyAfterTicketResolve = 'PatientCannotReplyAfterTicketResolve',
  EmailInUse = 'EmailInUse',
  MultipleSettingsEntriesFoundForInstitution = 'MultipleSettingsEntriesFoundForInstitution',
  InstitutionSettingsNotFound = 'InstitutionSettingsNotFound',
  NotOperatorTicket = 'NotOperatorTicket',
  PatientSMSRecentlySent = 'PatientSMSRecentlySent',
  PatientPhoneNumberNotFound = 'PatientPhoneNumberNotFound',
  PatientOptOut = 'PatientOptOut',
  PatientConversationAlreadyOpen = 'PatientConversationAlreadyOpen'
}

interface ErrorSpec {
  name: ErrorName;
  title?: string;
  description?: ReactNode;
}

export function getErrorSpecByServerErrorCode(errorCode: ErrorCode): ErrorSpec {
  switch (errorCode) {
    case ErrorCode.PatientDoesNotExist:
      return { name: ErrorName.PatientNoExist, title: 'Patient Does not Exist' };
    case ErrorCode.InvalidRequest:
      return { name: ErrorName.InvalidRequest, title: 'Invalid Request' };
    case ErrorCode.InvalidEmail:
      return { name: ErrorName.InvalidEmail, title: 'Invalid email' };
    case ErrorCode.InvalidPhone:
      return { name: ErrorName.InvalidPhone, title: 'Invalid phone' };
    case ErrorCode.NotValidPhoneForSms:
      return { name: ErrorName.NotMobilePhone, title: 'Landline phone number cannot be used' };
    case ErrorCode.DuplicatePhone:
      return { name: ErrorName.PhoneInUse, title: 'Phone Number in Use' };
    case ErrorCode.DuplicateMrn:
      return { name: ErrorName.MrnInUse, title: 'Medical Record Number in Use' };
    case ErrorCode.DuplicateEmail:
      return { name: ErrorName.EmailInUse, title: 'Email in Use' };
    case ErrorCode.ResolveTicketConflict:
      return {
        name: ErrorName.ResolveTicketsConflict,
        title: 'Cannot Resolve Ticket(s)',
        description: `One or more of the tickets you selected could not be resolved, because they are connected to other tickets by a logged call.\nTo proceed, resolve all connected tickets together using the checkmarks in the Patient Page, or disconnect the call from this ticket to resolve it individually.`
      };

    case ErrorCode.TicketConnectedToDraft:
      return {
        name: ErrorName.TicketConnectedToDraft,
        title: 'Disconnect Draft Call(s) to Resolve',
        description: `At least one of the tickets you are trying to resolve is connected to a Call Draft. Please submit that call, or resume and delete it, in order to resolve these ticket(s). `
      };
    case ErrorCode.ResolveClosedTicket:
      return {
        name: ErrorName.ResolveClosedTicket,
        title: 'Item(s) Already Resolved',
        description:
          'At least one of these item(s) have already been resolved. The page has been refreshed to reflect the latest changes.'
      };
    case ErrorCode.DuplicationError:
      return {
        name: ErrorName.DuplicationError,
        title: 'Phone or email address are already in use'
      };
    case ErrorCode.EpisodeDuplicateNameException:
      return { name: ErrorName.DuplicateEpisodeName, title: 'This episode name is already taken' };
    case ErrorCode.DuplicateEpisodeEndReason:
      return { name: ErrorName.DuplicateEpisodeEndReason, title: 'This reason already exists' };
    case ErrorCode.EpisodeInvalidDurationException:
      return {
        name: ErrorName.InvalidEpisodeDuration,
        title: 'Episode duration cannot be lower than the duration of the tasks in this episode'
      };
    case ErrorCode.EnrollEnrolledCMEpisode:
      return { name: ErrorName.EnrollInActiveCm };
    case ErrorCode.TicketDoublePickup:
      return { name: ErrorName.TicketDoublePickup };
    case ErrorCode.OverlappingCallIntervals:
      return { name: ErrorName.OverlappingIntervals };
    case ErrorCode.DuplicateReason:
      return {
        name: ErrorName.DuplicateReason,
        title: 'This reason already exists'
      };
    case ErrorCode.TimerIntervalNotWithinAllowedRange:
      return {
        name: ErrorName.IntervalOutOfAllowedScope
      };
    case ErrorCode.ThoughtSpotError:
      return {
        name: ErrorName.ThoughtSpot,
        title: 'Something Went Wrong',
        description:
          'There was an issue loading this content. Please try again in a few minutes. If the issue persists, reach out for support.'
      };
    case ErrorCode.SsoAuthenticatedButNotRegistered:
      return {
        name: ErrorName.SSOAuthenticatedButNotRegistered,
        title: 'No user found for this email.'
      };

    case ErrorCode.TicketSubTypeNameExists:
      return {
        name: ErrorName.DuplicateTicketSubTypeOperatorName,
        title: 'Name is already in use.'
      };

    case ErrorCode.TicketSubTypeTranslationNameExists:
      return {
        name: ErrorName.DuplicateTicketTypeDisplayNameForPatientApp,
        title: 'Name is already in use.'
      };

    case ErrorCode.ThoughtSpotNewUserError:
      return {
        name: ErrorName.ThoughtSpotNewUser,
        title: 'Authentication Issue',
        description:
          'This user was successfully created. However, we have run into an issue authenticating them for access to Practice Analytics.  Please reach out to support@canopycare.us (please include the email of this new user) and we will sort this out as quickly as possible.'
      };

    case ErrorCode.BulkInviteInProgress:
      return {
        name: ErrorName.BulkInviteInProgress,
        title: 'Invitation In Progress…',
        description:
          'An invitation to CCM or PCM is currently being sent to at least 1 of these patients. Please wait a moment before taking further action on these patient(s).'
      };

    case ErrorCode.SummaryCharacterLimit:
      return {
        name: ErrorName.SummaryCharacterLimit,
        title: 'Smart Summary is Too Long',
        description:
          'Please edit your Smart Summary, or click the Regenerate Smart Summary button to create a shorter summary.'
      };

    case ErrorCode.LandlineInsteadMobile:
      return {
        name: ErrorName.LandPhoneOnAutoProtocol,
        title: 'Cannot Invite Landline Phone Number',
        description: (
          <span>
            The invite SMS could not be sent, because the patient has a landline number.
            <br />
            <br /> Please edit their phone number in order to invite them to submit reports.
          </span>
        )
      };
    case ErrorCode.UserUnsubscribedFromSms:
      return {
        name: ErrorName.UserBlockSendingSms,
        title: 'This Number Has Blocked SMS from Canopy',
        description: (
          <span>
            This occurs when somebody has replied STOP to an SMS message from Canopy in the past.
            <br />
            <br />
            To un-block, this patient / Callback Contact must text START to the same number they
            previously blocked.
          </span>
        )
      };

    case ErrorCode.GenericSmsError:
      return {
        name: ErrorName.FailedToSendSms,
        title: 'Failed to send sms to patient'
      };
    case ErrorCode.DuplicateRoleError:
      return {
        name: ErrorName.DuplicateRoleName,
        title: 'Role Name already exists'
      };

    case ErrorCode.DuplicateEmrPatientFound:
      return {
        name: ErrorName.DuplicateEmrPatientFound
      };

    case ErrorCode.NoDuplicateEmrPatientFound:
      return {
        name: ErrorName.NoDuplicateEmrPatientFound
      };

    case ErrorCode.MultipleDuplicateEmrPatientFound:
      return {
        name: ErrorName.MultipleDuplicateEmrPatientFound
      };

    case ErrorCode.ResolvingUnreadInboundMessages:
      return {
        name: ErrorName.ResolvingUnreadInboundMessages,
        title: 'Unread Patient Message',
        description:
          'Tickets / tasks with unread patient messages cannot be resolved. Please review the message (by expanding the ticket) before resolving.'
      };

    case ErrorCode.ResolvingFailedOutboundMessages:
      return {
        name: ErrorName.ResolvingFailedOutboundMessages
      };

    case ErrorCode.ResolvingPendingOutboundMessages:
      return {
        name: ErrorName.ResolvingPendingOutboundMessages,
        title: 'Message to Patient Still Sending…',
        description:
          'At least one message is still sending. The delivery status should be updated within a few minutes.\n\n' +
          'Resolving now will remove the message from the ticket / task, and the resulting EMR PDF. '
      };

    case ErrorCode.PatientCannotReplyAfterTicketResolve:
      return {
        name: ErrorName.PatientCannotReplyAfterTicketResolve
      };

    case ErrorCode.MultipleSettingsEntriesFoundForInstitution:
      return {
        name: ErrorName.MultipleSettingsEntriesFoundForInstitution
      };

    case ErrorCode.InstitutionSettingsNotFound:
      return {
        name: ErrorName.InstitutionSettingsNotFound
      };

    case ErrorCode.NotOperatorTicket:
      return {
        name: ErrorName.NotOperatorTicket
      };

    case ErrorCode.PatientSMSRecentlySent:
      return {
        name: ErrorName.PatientSMSRecentlySent
      };

    case ErrorCode.PatientPhoneNumberNotFound:
      return {
        name: ErrorName.PatientPhoneNumberNotFound
      };

    case ErrorCode.PatientOptOut:
      return {
        name: ErrorName.PatientOptOut
      };

    case ErrorCode.PatientConversationAlreadyOpen:
      return {
        name: ErrorName.PatientConversationAlreadyOpen
      };

    default:
      return { name: ErrorName.HttpError };
  }
}

type ErrorDescriptionExtractor = (error: HttpError) => string;
type ErrorTransformer = (error: HttpError) => HttpError;

export function transformErrorUiProps(
  title?: string,
  description?: string | ErrorDescriptionExtractor
): ErrorTransformer {
  return (error: HttpError) => {
    if (title) {
      error.ui.title = title;
    }
    if (description) {
      error.ui.description = typeof description === 'string' ? description : description(error);
    }
    return error;
  };
}
export function transformErrorName(name: ErrorName): ErrorTransformer {
  return (error: HttpError) => {
    error.name = name;
    return error;
  };
}

// example - Error on entity Ticket (may be overridden on later stages, like HttpError.setHttpError or transformError)
export function getErrorTitle(
  entityName: string,
  method: Method,
  standardCrudMessage = false
): string {
  if (standardCrudMessage && isCrud(method)) {
    return getStandardCrudErrorMessage(method, entityName);
  }
  return getDefaultErrorTitle(entityName);
}

export function getStandardCrudErrorMessage(method: string, entityName: string): string {
  method = method.toUpperCase();

  switch (method) {
    case HTTP_METHODS.GET:
      return `Failed to fetch ${entityName}s`;
    case HTTP_METHODS.POST:
      return `Failed to create ${entityName}`;
    case HTTP_METHODS.PUT:
      return `Failed to update ${entityName}`;
    case HTTP_METHODS.DELETE:
      return `Failed to delete ${entityName}`;
    default:
      return getDefaultErrorTitle(entityName);
  }
}

export function getDefaultErrorTitle(entityName: string): string {
  return `Error on entity: ${entityName}`;
}

export const CLINICIAN_EDIT_CREATE_ERRORS = [
  ErrorName.DuplicationError,
  ErrorName.NotMobilePhone,
  ErrorName.InvalidPhone,
  ErrorName.InvalidEmail
];
